import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import { Modal } from './Modal.js';
import { PROVIDER_FIELDS } from './providers/field-defs.js';
import { useConnections, type ConnectionListItem } from './use-integrations.js';

const ALL_PROVIDERS = ['bamboohr', 'docusign', 'graph', 'jira', 'email'] as const;

export function IntegrationsListPage(): JSX.Element {
  const { companyId } = useParams<{ companyId: string }>();
  const { data, isLoading, error } = useConnections(companyId);
  const [addingProvider, setAddingProvider] = useState<string | null>(null);
  const navigate = useNavigate();

  const grouped = useMemo(() => {
    const m = new Map<string, ConnectionListItem[]>();
    for (const p of ALL_PROVIDERS) m.set(p, []);
    for (const c of data?.items ?? []) {
      const list = m.get(c.provider) ?? [];
      list.push(c);
      m.set(c.provider, list);
    }
    return m;
  }, [data]);

  if (isLoading) return <p className="text-slate-500">Loading integrations…</p>;
  if (error) {
    return (
      <div className="rounded-md border border-red-300 bg-red-50 p-4 text-red-800">
        <p className="font-medium">Could not load integrations.</p>
        <p className="text-sm">{error.message}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Integrations</h1>
          <p className="mt-1 text-sm text-slate-600">
            Every provider has a <span className="font-mono">default</span> connection. Add a named
            connection to run a specific module against a separate account (e.g.{' '}
            <span className="font-mono">legal</span> DocuSign for Termination).
          </p>
        </div>
        <Link to="/" className="text-sm text-brand-600 hover:text-brand-700">
          ← Back
        </Link>
      </div>

      <div className="space-y-6">
        {ALL_PROVIDERS.map((p) => {
          const meta = PROVIDER_FIELDS[p];
          const conns = grouped.get(p) ?? [];
          const hasDefault = conns.some((c) => c.name === 'default');
          return (
            <section key={p} className="rounded-lg border border-slate-200 bg-white">
              <header className="flex items-start justify-between border-b border-slate-200 px-4 py-3">
                <div>
                  <h2 className="font-semibold text-slate-900">{meta.title}</h2>
                  <p className="mt-0.5 text-xs text-slate-500">{meta.description}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setAddingProvider(p)}
                  className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium hover:bg-slate-50"
                >
                  + Add connection
                </button>
              </header>
              <ul className="divide-y divide-slate-100">
                {conns.length === 0 && (
                  <li className="px-4 py-3 text-sm text-slate-500">
                    No <span className="font-mono">default</span> connection yet — click Add, name
                    it <span className="font-mono">default</span>, and save.
                  </li>
                )}
                {!hasDefault && conns.length > 0 && (
                  <li className="px-4 py-2 text-xs text-amber-800">
                    No <span className="font-mono">default</span> connection configured yet. Modules
                    bind to <span className="font-mono">default</span> unless overridden.
                  </li>
                )}
                {conns.map((c) => (
                  <li key={c.name}>
                    <Link
                      to={`/companies/${companyId}/integrations/${p}/${c.name}`}
                      className="flex items-center justify-between px-4 py-3 hover:bg-slate-50"
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          {c.name}
                          {c.isDefault && (
                            <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-xs font-normal text-slate-600">
                              default
                            </span>
                          )}
                        </p>
                        <p className="mt-0.5 text-xs text-slate-500">
                          Last tested {formatDate(c.lastTestedAt)} · Last rotated{' '}
                          {formatDate(c.lastRotatedAt)}
                          {c.lastTestError && (
                            <span className="ml-2 text-red-700">· {c.lastTestError}</span>
                          )}
                        </p>
                      </div>
                      <StatusPill status={c.status} />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      <AddConnectionModal
        open={addingProvider !== null}
        provider={addingProvider}
        onClose={() => setAddingProvider(null)}
        onSubmit={(name) => {
          if (addingProvider) {
            navigate(`/companies/${companyId}/integrations/${addingProvider}/${name}`);
          }
          setAddingProvider(null);
        }}
      />
    </div>
  );
}

function AddConnectionModal({
  open,
  provider,
  onClose,
  onSubmit,
}: {
  open: boolean;
  provider: string | null;
  onClose: () => void;
  onSubmit: (name: string) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const ok = /^[a-z0-9][a-z0-9-]{0,30}$/.test(name);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={provider ? `Add ${provider} connection` : 'Add connection'}
    >
      <p className="mb-3 text-sm text-slate-600">
        Pick a short, lowercase name. Use <span className="font-mono">default</span> for the primary
        connection, or something descriptive (<span className="font-mono">legal</span>,{' '}
        <span className="font-mono">hr</span>, <span className="font-mono">canada</span>) for
        additional ones.
      </p>
      <label className="block text-sm font-medium text-slate-800">Connection name</label>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value.toLowerCase())}
        placeholder="default"
        className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
      />
      <p className="mt-1 text-xs text-slate-500">Lowercase letters, digits, dashes. 1–31 chars.</p>
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!ok}
          onClick={() => onSubmit(name)}
          className={clsx(
            'rounded-md px-3 py-1.5 text-sm font-medium text-white shadow-sm',
            ok ? 'bg-brand-600 hover:bg-brand-700' : 'bg-slate-300 cursor-not-allowed',
          )}
        >
          Continue
        </button>
      </div>
    </Modal>
  );
}

function StatusPill({ status }: { status: ConnectionListItem['status'] }): JSX.Element {
  const styles: Record<ConnectionListItem['status'], string> = {
    unconfigured: 'bg-slate-100 text-slate-700',
    connected: 'bg-green-100 text-green-800',
    failing: 'bg-red-100 text-red-800',
  };
  return (
    <span
      className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', styles[status])}
    >
      {status}
    </span>
  );
}

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  return new Date(s).toLocaleString();
}
